import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import * as PropTypes from "prop-types";
import React from "react";
import { withTranslation } from "react-i18next";
import { maximumYear, minimumYear } from "../../constants";
import { translateGridText } from "../../i18n";
import { formatNumericCell, parseNullableAlphaCell, parseNullableNumericCell, parseNumericCell } from "../../utilities";
import BooleanCellRenderer from "../BooleanCellRenderer";

const columnDefinitions = (t) => [
    {
        headerName: t("Include?"),
        field: "include",
        cellRenderer: "booleanCellRenderer",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            cellRenderer: "booleanCellRenderer",
            values: [true, false],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Possible outlier"),
        field: "possible_outlier",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["0", "1", null],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Possible outlier (user input)"),
        field: "possible_outlier_userinput",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["0", "1", null],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Region code"),
        field: "regionCode",
        valueParser: (params) => parseNullableAlphaCell(params),
    },
    {
        headerName: t("Start year"),
        field: "startDate",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        valueParser: (params) => parseNumericCell(params, minimumYear + 0.5, params.node.data.endDate),
    },
    {
        headerName: t("End year"),
        field: "endDate",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        valueParser: (params) => parseNumericCell(params, params.node.data.startDate, maximumYear + 0.5),
    },
    {
        headerName: t("In union?"),
        field: "isInUnion",
        cellRenderer: "booleanCellRenderer",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            cellRenderer: "booleanCellRenderer",
            values: [true, false],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Age"),
        field: "ageRange",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["10-14", "15-19", "20-24", "25-29", "30-34", "35-39", "40-44", "45-49", "15-49"],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Data series/type"),
        field: "dataSeriesType",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["DHS", "MICS", "National survey", "PMA", "Other"],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Population"),
        field: "groupTypeRelativeToBaseline",
        headerTooltip: t("Group type relative to baseline"),
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["AL", "BS", "EM", "FM", "HW", "MW", "PW", "SA", "UW"],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Modern contraceptive use"),
        field: "contraceptiveUseModern",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        valueParser: (params) => parseNumericCell(params, 0, 1),
        headerTooltip: t("Contraceptive use (modern)"),
    },
    {
        headerName: t("Traditional contraceptive use"),
        field: "contraceptiveUseTraditional",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        valueParser: (params) => parseNumericCell(params, 0, 1),
        headerTooltip: t("Contraceptive use (traditional)"),
    },
    {
        headerName: t("All contraceptive use"),
        field: "contraceptiveUseAny",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        valueParser: (params) => parseNumericCell(params, 0, 1),
        headerTooltip: t("Contraceptive use (all)"),
    },
    {
        headerName: t("Modern methods unmet need"),
        field: "unmetNeedModern",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        valueParser: (params) => parseNullableNumericCell(params, 0, 1),
        headerTooltip: t("Unmet need for modern methods"),
    },
    {
        headerName: t("Any method unmet need"),
        field: "unmetNeedAny",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        valueParser: (params) => parseNullableNumericCell(params, 0, 1),
        headerTooltip: t("Unmet need for any method"),
    },
    {
        headerName: t("Pertaining to methods used since last pregnancy"),
        field: "pertainingToMethodsUsedSinceLastPregnancyReason",
    },
    {
        headerName: t("Geographical bias"),
        field: "geographicalRegionBiasReason",
    },
    {
        headerName: t("Non-pregnant and other positive biases"),
        field: "nonPregnantAndOtherPositiveBiasesReason",
    },
    {
        headerName: t("Age bias"),
        field: "ageGroupBias",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["+", "-", "?", "None"],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Modern method bias"),
        field: "modernMethodBias",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: ["+", "-", "None"],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("Traditional method bias"),
        field: "traditionalMethodBiasReason",
    },
    {
        headerName: t("Absence of probing question bias?"),
        field: "hasAbsenceOfProbingQuestionsBias",
        cellRenderer: "booleanCellRenderer",
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            cellRenderer: "booleanCellRenderer",
            values: [true, false],
        },
        cellEditorPopup: true,
    },
    {
        headerName: t("SE log-ratio modern/no use"),
        field: "seLogRModernNoUse",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        valueParser: (params) => parseNullableNumericCell(params, 0, Number.MAX_VALUE),
        headerTooltip: t("Sampling error for the log-ratio of modern contraceptive method use to no use"),
    },
    {
        headerName: t("SE log-ratio traditional/no use"),
        field: "seLogRTraditionalNoUse",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        valueParser: (params) => parseNullableNumericCell(params, 0, Number.MAX_VALUE),
        headerTooltip: t("Sampling error for the log-ratio of traditional contraceptive method use to no use"),
    },
    {
        headerName: t("SE log-ratio unmet/no need"),
        field: "seLogRUnmetNoNeed",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        valueParser: (params) => parseNullableNumericCell(params, 0, Number.MAX_VALUE),
        headerTooltip: t("Sampling error for the log-ratio of unmet need (any method) to no need"),
    },
    {
        headerName: t("Source ID"),
        field: "sourceId",
        type: "numericColumn",
    },
];

const PrepareDataSurveyDataTable = ({ t, ...rest }) => {
    const onGridReady = (params) => {
        rest.onGridReady(params);
        params.columnApi.autoSizeAllColumns();
    };

    const onSelectionChanged = () => {
        rest.onSelectedChanged();
    };

    const refreshCells = (params) => {
        params.gridApi.refreshCells();
    };

    const components = React.useMemo(
        () => ({
            booleanCellRenderer: BooleanCellRenderer,
        }),
        []
    );

    const rowData = rest.data;
    const columnDefs = columnDefinitions(t);

    return (
        <div className="ag-theme-material">
            <AgGridReact
                domLayout="autoHeight"
                defaultColDef={{ sortable: true, editable: true }}
                pagination={true}
                paginationPageSize={10}
                suppressColumnVirtualisation={true}
                suppressCellFocus={true}
                components={components}
                overlayNoRowsTemplate="<span />"
                getLocaleText={translateGridText}
                rowData={rowData}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                rowSelection="single"
                onSelectionChanged={onSelectionChanged}
                stopEditingWhenCellsLoseFocus={true}
                rowHeight={30}
                onCellValueChanged={(params) => {
                    rest.onEditItem(params.data, params.colDef.field, refreshCells);
                }}
            />
        </div>
    );
};

PrepareDataSurveyDataTable.propTypes = {
    data: PropTypes.array,
};

PrepareDataSurveyDataTable.defaultProps = {
    data: [],
};

export default withTranslation()(PrepareDataSurveyDataTable);
